import React, { useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import { useClient } from '../context/ClientContext';
import { useAuth } from '../context/AuthContext';
import { 
  PlusCircle, Upload, Download, FileSpreadsheet, 
  CheckCircle, AlertTriangle, Calculator, Building2,
  Trash2, IndianRupee, Percent, Calendar, FileText
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Common TDS/TCS Sections - FY 2025-26
const TDS_SECTIONS = [
  // TDS Sections
  { value: '194C', label: '194C - Contractors/Sub-contractors', rate: '1%/2%' },
  { value: '194J', label: '194J - Professional Fees (10%)', rate: '10%' },
  { value: '194J_technical', label: '194J - Technical/Royalty/Call Center (2%)', rate: '2%' },
  { value: '194H', label: '194H - Commission/Brokerage', rate: '2%' },
  { value: '194I(a)', label: '194I(a) - Rent (Plant/Machinery)', rate: '2%' },
  { value: '194I(b)', label: '194I(b) - Rent (Building/Land)', rate: '10%' },
  { value: '194A', label: '194A - Interest (non-bank)', rate: '10%' },
  { value: '194T', label: '194T - Payment to Partners', rate: '10%' },
  { value: '194R', label: '194R - Benefits/Perquisites', rate: '10%' },
  { value: '194Q', label: '194Q - Purchase of Goods (>50L)', rate: '0.1%' },
  { value: '194', label: '194 - Dividend', rate: '10%' },
  { value: '194D', label: '194D - Insurance Commission', rate: '5%' },
  { value: '194B', label: '194B - Lottery/Crossword', rate: '30%' },
  { value: '194BA', label: '194BA - Online Gaming', rate: '30%' },
  { value: '194O', label: '194O - E-commerce', rate: '1%' },
  { value: '194S', label: '194S - Virtual Digital Assets (Crypto)', rate: '1%' },
  { value: '194N', label: '194N - Cash Withdrawal (>1Cr)', rate: '2%/5%' },
  { value: '194IA', label: '194IA - Immovable Property', rate: '1%' },
  { value: '194IB', label: '194IB - Rent by Individual/HUF', rate: '5%' },
  { value: '194M', label: '194M - Contract by Individual/HUF', rate: '5%' },
  // TCS Sections
  { value: '206C(1)', label: '206C(1) - TCS Sale of Scrap', rate: '1%' },
  { value: '206C(1F)', label: '206C(1F) - TCS Motor Vehicle (>10L)', rate: '1%' },
  { value: '206C(1F)(a)', label: '206C(1F)(a) - TCS Foreign Remittance (LRS)', rate: '5%/20%' },
  { value: '206C(1F)(f)', label: '206C(1F)(f) - TCS Overseas Tour Package', rate: '5%' },
  { value: '206C(1H)', label: '206C(1H) - TCS Sale of Goods (>50L)', rate: '0.1%' },
  { value: 'OTHER', label: 'Other', rate: '-' },
];

const ManualEntry = () => {
  const { selectedClient, clientId } = useClient();
  const { canEdit } = useAuth();
  const [activeTab, setActiveTab] = useState('single');
  const [loading, setLoading] = useState(false);
  
  // Single entry form
  const [formData, setFormData] = useState({
    party_name: '',
    pan: '',
    amount: '',
    tds_amount: '',
    section: '194C',
    date: new Date().toISOString().split('T')[0],
    remarks: ''
  });
  
  // Bulk upload
  const [bulkData, setBulkData] = useState([]);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [uploadResult, setUploadResult] = useState(null);

  // Calculate TDS rate
  const calculateRate = () => {
    const amount = parseFloat(formData.amount) || 0;
    const tds = parseFloat(formData.tds_amount) || 0;
    if (amount > 0 && tds > 0) {
      return ((tds / amount) * 100).toFixed(2);
    }
    return '0.00';
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSingleSubmit = async (e) => {
    e.preventDefault();
    
    if (!clientId) {
      toast.error('Please select a client first');
      return;
    }
    
    if (!formData.party_name.trim()) {
      toast.error('Party name is required');
      return;
    }
    
    const amount = parseFloat(formData.amount);
    const tdsAmount = parseFloat(formData.tds_amount);
    
    if (!amount || amount <= 0) {
      toast.error('Valid expense amount is required');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/api/transactions/manual`, {
        party_name: formData.party_name.trim(),
        pan: formData.pan.trim() || null,
        amount: amount,
        tds_amount: tdsAmount || 0,
        section: formData.section,
        date: formData.date || null,
        remarks: formData.remarks.trim() || null
      }, { params: { client_id: clientId } });
      
      toast.success('Transaction added successfully');
      
      // Reset form
      setFormData({
        party_name: '',
        pan: '',
        amount: '',
        tds_amount: '',
        section: '194C',
        date: new Date().toISOString().split('T')[0],
        remarks: ''
      });
      
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add transaction');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateDownload = async () => {
    try {
      const response = await axios.get(`${API}/api/transactions/manual/template`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'TDS_Manual_Entry_Template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Template downloaded');
    } catch (err) {
      toast.error('Failed to download template');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadErrors([]);
    setUploadResult(null);
    
    try {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const workbook = XLSX.read(event.target.result, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          
          // Skip header row
          const dataRows = jsonData.slice(1).filter(row => 
            row.length > 0 && row[0] && String(row[0]).trim()
          );
          
          const parsedData = dataRows.map((row, idx) => ({
            rowNum: idx + 2,
            party_name: String(row[0] || '').trim(),
            pan: String(row[1] || '').trim().toUpperCase(),
            amount: parseFloat(row[2]) || 0,
            tds_amount: parseFloat(row[3]) || 0,
            section: String(row[4] || '194C').trim(),
            date: row[5] ? String(row[5]).trim() : null,
            remarks: String(row[6] || '').trim()
          }));
          
          // Validate
          const errors = [];
          parsedData.forEach(item => {
            if (!item.party_name) {
              errors.push(`Row ${item.rowNum}: Party name is required`);
            }
            if (item.amount <= 0) {
              errors.push(`Row ${item.rowNum}: Amount must be greater than 0`);
            }
          });
          
          if (errors.length > 0) {
            setUploadErrors(errors);
          }
          
          setBulkData(parsedData);
          toast.success(`Loaded ${parsedData.length} transactions from file`);
          
        } catch (parseErr) {
          toast.error('Failed to parse Excel file');
        }
      };
      
      reader.readAsBinaryString(file);
    } catch (err) {
      toast.error('Failed to read file');
    }
    
    // Reset file input
    e.target.value = '';
  };

  const handleBulkSubmit = async () => {
    if (!clientId) {
      toast.error('Please select a client first');
      return;
    }
    
    if (bulkData.length === 0) {
      toast.error('No transactions to upload');
      return;
    }
    
    // Filter out invalid rows
    const validData = bulkData.filter(item => 
      item.party_name && item.amount > 0
    );
    
    if (validData.length === 0) {
      toast.error('No valid transactions found');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/api/transactions/manual/bulk`, {
        transactions: validData.map(item => ({
          party_name: item.party_name,
          pan: item.pan || null,
          amount: item.amount,
          tds_amount: item.tds_amount,
          section: item.section,
          date: item.date,
          remarks: item.remarks
        }))
      }, { params: { client_id: clientId } });
      
      setUploadResult(response.data);
      
      if (response.data.errors && response.data.errors.length > 0) {
        setUploadErrors(response.data.errors);
      }
      
      toast.success(`Successfully added ${response.data.inserted} transactions`);
      setBulkData([]);
      
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload transactions');
    } finally {
      setLoading(false);
    }
  };

  const removeBulkRow = (idx) => {
    setBulkData(prev => prev.filter((_, i) => i !== idx));
  };

  if (!canEdit()) {
    return (
      <div className="p-6">
        <Card className="max-w-md mx-auto">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="w-12 h-12 mx-auto text-yellow-500 mb-4" />
            <h2 className="text-xl font-semibold mb-2">View Only Access</h2>
            <p className="text-slate-500">You don't have permission to add transactions.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <PlusCircle className="w-7 h-7 text-blue-600" />
            Manual TDS Entry
          </h1>
          <p className="text-slate-500">Add missed TDS transactions manually or via Excel upload</p>
        </div>
        {selectedClient && (
          <div className="flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-lg">
            <Building2 className="w-4 h-4 text-blue-600" />
            <span className="font-medium text-blue-900">{selectedClient.name}</span>
          </div>
        )}
      </div>

      {!clientId && (
        <Alert variant="destructive">
          <AlertTriangle className="w-4 h-4" />
          <AlertDescription>Please select a client from the sidebar to add transactions.</AlertDescription>
        </Alert>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="single" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Single Entry
          </TabsTrigger>
          <TabsTrigger value="bulk" className="flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4" />
            Bulk Upload
          </TabsTrigger>
        </TabsList>

        {/* Single Entry Tab */}
        <TabsContent value="single" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Add Single Transaction</CardTitle>
              <CardDescription>Enter details for a missed TDS transaction</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSingleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Party Name */}
                  <div className="space-y-2">
                    <Label htmlFor="party_name">Party Name *</Label>
                    <Input
                      id="party_name"
                      placeholder="Enter party/vendor name"
                      value={formData.party_name}
                      onChange={(e) => handleInputChange('party_name', e.target.value)}
                      required
                      disabled={!clientId}
                    />
                  </div>

                  {/* PAN */}
                  <div className="space-y-2">
                    <Label htmlFor="pan">PAN</Label>
                    <Input
                      id="pan"
                      placeholder="AAAAA1234A"
                      maxLength={10}
                      value={formData.pan}
                      onChange={(e) => handleInputChange('pan', e.target.value.toUpperCase())}
                      disabled={!clientId}
                    />
                  </div>

                  {/* Expense Amount */}
                  <div className="space-y-2">
                    <Label htmlFor="amount">Expense Amount *</Label>
                    <div className="relative">
                      <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="amount"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="100000"
                        className="pl-9"
                        value={formData.amount}
                        onChange={(e) => handleInputChange('amount', e.target.value)}
                        required
                        disabled={!clientId}
                      />
                    </div>
                  </div>

                  {/* TDS Amount */}
                  <div className="space-y-2">
                    <Label htmlFor="tds_amount">TDS Amount</Label>
                    <div className="relative">
                      <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="tds_amount"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="2000"
                        className="pl-9"
                        value={formData.tds_amount}
                        onChange={(e) => handleInputChange('tds_amount', e.target.value)}
                        disabled={!clientId}
                      />
                    </div>
                  </div>

                  {/* TDS Rate (Calculated) */}
                  <div className="space-y-2">
                    <Label>TDS Rate (Auto-calculated)</Label>
                    <div className="flex items-center gap-2 h-10 px-3 bg-slate-100 rounded-md">
                      <Percent className="w-4 h-4 text-slate-500" />
                      <span className="font-mono font-semibold">{calculateRate()}%</span>
                    </div>
                  </div>

                  {/* Section */}
                  <div className="space-y-2">
                    <Label htmlFor="section">TDS Section *</Label>
                    <Select 
                      value={formData.section} 
                      onValueChange={(v) => handleInputChange('section', v)}
                      disabled={!clientId}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select section" />
                      </SelectTrigger>
                      <SelectContent>
                        {TDS_SECTIONS.map(s => (
                          <SelectItem key={s.value} value={s.value}>
                            {s.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Date */}
                  <div className="space-y-2">
                    <Label htmlFor="date">Transaction Date</Label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="date"
                        type="date"
                        className="pl-9"
                        value={formData.date}
                        onChange={(e) => handleInputChange('date', e.target.value)}
                        disabled={!clientId}
                      />
                    </div>
                  </div>

                  {/* Remarks */}
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="remarks">Remarks</Label>
                    <Textarea
                      id="remarks"
                      placeholder="Add any notes about this transaction..."
                      rows={2}
                      value={formData.remarks}
                      onChange={(e) => handleInputChange('remarks', e.target.value)}
                      disabled={!clientId}
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  disabled={loading || !clientId}
                  className="w-full md:w-auto"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <PlusCircle className="w-4 h-4 mr-2" />
                      Add Transaction
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bulk Upload Tab */}
        <TabsContent value="bulk" className="mt-6 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Bulk Upload via Excel</CardTitle>
              <CardDescription>Download the template, fill in your data, and upload</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Template Download */}
              <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-lg">
                <FileSpreadsheet className="w-10 h-10 text-blue-600" />
                <div className="flex-1">
                  <h3 className="font-semibold">Download Template</h3>
                  <p className="text-sm text-slate-600">Get the Excel template with all required columns and examples</p>
                </div>
                <Button variant="outline" onClick={handleTemplateDownload}>
                  <Download className="w-4 h-4 mr-2" />
                  Download Template
                </Button>
              </div>

              {/* File Upload */}
              <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
                <Upload className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                <h3 className="font-semibold mb-2">Upload Excel File</h3>
                <p className="text-sm text-slate-500 mb-4">Supported format: .xlsx, .xls</p>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="bulk-file-input"
                  disabled={!clientId}
                />
                <label htmlFor="bulk-file-input">
                  <Button variant="outline" asChild disabled={!clientId}>
                    <span className="cursor-pointer">
                      <Upload className="w-4 h-4 mr-2" />
                      Choose File
                    </span>
                  </Button>
                </label>
              </div>
            </CardContent>
          </Card>

          {/* Upload Errors */}
          {uploadErrors.length > 0 && (
            <Alert variant="destructive">
              <AlertTriangle className="w-4 h-4" />
              <AlertDescription>
                <p className="font-semibold mb-2">Validation Errors:</p>
                <ul className="list-disc list-inside text-sm max-h-32 overflow-y-auto">
                  {uploadErrors.map((err) => (
                    <li key={err}>{err}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* Upload Result */}
          {uploadResult && (
            <Alert className="bg-green-50 border-green-200">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <AlertDescription className="text-green-800">
                <p className="font-semibold">Upload Successful!</p>
                <p>Added {uploadResult.inserted} transactions totaling ₹{uploadResult.total_amount?.toLocaleString('en-IN')} with TDS of ₹{uploadResult.total_tds?.toLocaleString('en-IN')}</p>
              </AlertDescription>
            </Alert>
          )}

          {/* Preview Data */}
          {bulkData.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Preview ({bulkData.length} transactions)</span>
                  <Button onClick={handleBulkSubmit} disabled={loading || !clientId}>
                    {loading ? 'Uploading...' : 'Upload All'}
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto max-h-96">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="p-2 text-left">#</th>
                        <th className="p-2 text-left">Party Name</th>
                        <th className="p-2 text-left">PAN</th>
                        <th className="p-2 text-right">Amount</th>
                        <th className="p-2 text-right">TDS</th>
                        <th className="p-2 text-right">Rate</th>
                        <th className="p-2 text-left">Section</th>
                        <th className="p-2"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {bulkData.map((item, idx) => {
                        const rate = item.amount > 0 ? ((item.tds_amount / item.amount) * 100).toFixed(2) : '0.00';
                        const hasError = !item.party_name || item.amount <= 0;
                        
                        return (
                          <tr key={`${item.rowNum}-${item.party_name}-${item.amount}`} className={hasError ? 'bg-red-50' : 'hover:bg-slate-50'}>
                            <td className="p-2 text-slate-500">{item.rowNum}</td>
                            <td className="p-2 font-medium">{item.party_name || <span className="text-red-500">Missing</span>}</td>
                            <td className="p-2 font-mono text-xs">{item.pan || '-'}</td>
                            <td className="p-2 text-right">₹{item.amount.toLocaleString('en-IN')}</td>
                            <td className="p-2 text-right">₹{item.tds_amount.toLocaleString('en-IN')}</td>
                            <td className="p-2 text-right font-mono">{rate}%</td>
                            <td className="p-2">{item.section}</td>
                            <td className="p-2">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => removeBulkRow(idx)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ManualEntry;
